import React from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useStore } from '../../../store';
import { signOut } from '../../../~reusables/firebase';
import { goToAdminLogin } from '../../../~reusables/actions';
import { followMenuItemLink, TransitionLink } from '../../atoms/TransitionLink';
import styled from '../../../~reusables/contexts/ThemeContext';
import { getAccountDisplayName } from '../../../~reusables/util/accountDisplayName';
import { TeamLogo } from '../../atoms/TeamLogo';
import { getTeamRoleLabel } from '../../../~reusables/util/teams';
import {
  LEGACY_ACCOUNT_SETTINGS_TABS,
  PROFILE_SETTINGS_TABS,
  TEAM_SETTINGS_TABS,
} from './navModel';
import { useFeatureFlag } from '../../../~reusables/hooks/useFeatureFlag';
import { AvatarButton } from './NavStyles';
import { ThemeMenuRow } from './ThemeMenuRow';

export const getUserDisplayName = (user: {
  firstName?: string;
  lastName?: string;
  email?: string;
}): string => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return fullName || user.email?.split('@')[0] || 'Account';
};

export const getUserInitials = (user: {
  firstName?: string;
  lastName?: string;
  email?: string;
}): string => {
  const fromName = [user.firstName, user.lastName]
    .filter((part): part is string => Boolean(part))
    .map((part) => part[0])
    .join('');
  const initials = fromName || user.email?.[0] || '?';
  return initials.slice(0, 2).toUpperCase();
};

export const AccountMenu: React.FC = () => {
  const user = useStore((state) => state.user);
  const joinedTeams = useStore((state) => state.acceptedUsers);
  const hasTeams = useFeatureFlag('accountTeams');
  const canPickTheme = useFeatureFlag('systemTheme');
  // Signing is part of v2: role labels mention it only with the flag.
  const v2Apps = useFeatureFlag('v2Apps');

  if (!user) return null;

  const displayName = getUserDisplayName(user);
  const teamName = getAccountDisplayName(user);

  const settingsItems: NonNullable<MenuProps['items']> = hasTeams
    ? [
        {
          key: 'profile',
          type: 'group',
          label: 'Your profile',
          children: PROFILE_SETTINGS_TABS.map((tab) => ({
            key: tab.key,
            label: <TransitionLink to={tab.to}>{tab.label}</TransitionLink>,
          })),
        },
        { type: 'divider', key: 'divider-team' },
        {
          key: 'teams',
          type: 'group',
          label: 'Teams',
          children: [
            {
              key: 'team-settings',
              label: (
                <TransitionLink to={TEAM_SETTINGS_TABS[0].to}>
                  <TeamMenuRow>
                    <TeamLogo
                      teamId={user.id}
                      name={teamName}
                      logoUrl={user.accountLogoUrl}
                      size={20}
                    />
                    <TeamMenuName>{teamName}</TeamMenuName>
                    <TeamMenuMeta>Owner</TeamMenuMeta>
                  </TeamMenuRow>
                </TransitionLink>
              ),
            },
            ...joinedTeams.map((team) => {
              const name = getAccountDisplayName(team);
              return {
                key: `team-${team.id}`,
                label: (
                  <TransitionLink
                    to={`${TEAM_SETTINGS_TABS[0].to}?${new URLSearchParams({ account: team.id })}`}
                  >
                    <TeamMenuRow>
                      <TeamLogo
                        teamId={team.id}
                        name={name}
                        logoUrl={team.accountLogoUrl}
                        size={20}
                      />
                      <TeamMenuName>{name}</TeamMenuName>
                      <TeamMenuMeta>
                        {getTeamRoleLabel({
                          isOwner: false,
                          permissions: team.permissions,
                          signing: v2Apps,
                        })}
                      </TeamMenuMeta>
                    </TeamMenuRow>
                  </TransitionLink>
                ),
              };
            }),
            {
              key: 'create-team',
              label: (
                <TransitionLink to="/teams/new">Create team</TransitionLink>
              ),
            },
          ],
        },
      ]
    : [
        {
          key: 'account',
          type: 'group',
          label: 'Account',
          children: LEGACY_ACCOUNT_SETTINGS_TABS.map((tab) => ({
            key: tab.key,
            label: <TransitionLink to={tab.to}>{tab.label}</TransitionLink>,
          })),
        },
      ];

  const items: MenuProps['items'] = [
    ...settingsItems,
    ...(user.isAdmin
      ? ([
          { type: 'divider', key: 'divider-admin' },
          {
            key: 'admin',
            type: 'group',
            label: 'Admin',
            children: [
              {
                key: 'admin-pulse',
                label: <TransitionLink to="/super-admin">Pulse</TransitionLink>,
              },
              {
                key: 'view-customers',
                label: (
                  <TransitionLink to="/super-admin/customers">
                    Customers
                  </TransitionLink>
                ),
              },
              {
                key: 'admin-inbox',
                label: (
                  <TransitionLink to="/super-admin/inbox">Inbox</TransitionLink>
                ),
              },
              {
                key: 'feature-flags',
                label: (
                  <TransitionLink to="/super-admin/feature-flags">
                    Feature flags
                  </TransitionLink>
                ),
              },
              {
                key: 'impersonate-user',
                label: 'Impersonate user',
                onClick: () => goToAdminLogin(),
              },
            ],
          },
        ] as NonNullable<MenuProps['items']>)
      : []),
    ...(canPickTheme
      ? ([
          { type: 'divider', key: 'divider-theme' },
          { key: 'theme', type: 'group', label: <ThemeMenuRow /> },
        ] as NonNullable<MenuProps['items']>)
      : []),
    { type: 'divider', key: 'divider-logout' },
    { key: 'logout', label: 'Log out', onClick: () => signOut() },
  ];

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{ items, style: { minWidth: 240 }, onClick: followMenuItemLink }}
      popupRender={(menu) => (
        <AccountPopup>
          <AccountIdentity>
            <AccountName>{displayName}</AccountName>
            {user.email && <AccountEmail>{user.email}</AccountEmail>}
          </AccountIdentity>
          {menu}
        </AccountPopup>
      )}
    >
      <AvatarButton
        type="button"
        aria-label={`Account menu for ${displayName}`}
        title={displayName}
      >
        {user.avatar ? <img src={user.avatar} alt="" /> : getUserInitials(user)}
      </AvatarButton>
    </Dropdown>
  );
};

const TeamMenuRow = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const TeamMenuName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TeamMenuMeta = styled.span`
  flex: none;
  color: var(--nav-text-subtle);
  font-size: 12px;
`;

const AccountPopup = styled.div`
  min-width: 240px;
  border-radius: 8px;
  background: var(--layout-card-bg);
  box-shadow:
    0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);

  .ant-dropdown-menu {
    box-shadow: none;
  }
`;

const AccountIdentity = styled.div`
  padding: 12px 16px 8px;
  border-bottom: 1px solid var(--layout-card-border);
`;

const AccountName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--nav-text);
`;

const AccountEmail = styled.div`
  font-size: 12px;
  color: var(--nav-text-subtle);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
`;
